import React from 'react'
import {
    Button,
    IconButton,
    Popover,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import { Container } from '../../../../../components'
import { PopoverState } from '../types'
import {
    LeaseAudit,
    LeaseAuditSchedule,
    LeaseAuditEvent,
} from '../../../../../models'
import DeleteIcon from '@material-ui/icons/Delete'
import PauseIcon from '@material-ui/icons/Pause'
import PlayArrowIcon from '@material-ui/icons/PlayArrow'
import { typographyStyle } from '../../../../../theme/typographyStyle'

interface Props {
    formState: PopoverState
    schedule: LeaseAuditSchedule | null
    leaseAudit: LeaseAudit | null
    event: LeaseAuditEvent | null
    onClose: () => void
    onScheduleClick: () => void
    onEditClick: (schedule: LeaseAuditSchedule) => void
    onDetailClick: (schedule: LeaseAuditSchedule) => void
    onLeaseAuditDetailClick: (leaseAudit: LeaseAudit) => void
    onEventDetailClick?: (event: LeaseAuditEvent) => void
    onPauseSchedule?: (schedule: LeaseAuditSchedule) => void
    onDeleteSchedule?: (schedule: LeaseAuditSchedule) => void
    onResumeSchedule?: (schedule: LeaseAuditSchedule) => void
}

export const DateClickPopover: React.FC<Props> = ({
    formState,
    schedule,
    leaseAudit,
    event,
    onClose,
    onScheduleClick,
    onEditClick,
    onDetailClick,
    onLeaseAuditDetailClick,
    onEventDetailClick,
    onPauseSchedule,
    onDeleteSchedule,
    onResumeSchedule,
}) => {
    const theme = useTheme()

    const scheduleDate = schedule?.start_date
        ? new Date(schedule.start_date)
        : formState.date

    // Helper function to render content based on type
    const renderContent = () => {
        if (event) {
            return (
                <Container
                    style={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        flexDirection: 'column',
                        padding: theme.spacing(1),
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                        }}
                    >
                        <Tooltip title="View Event Details">
                            <IconButton
                                onClick={() => onEventDetailClick?.(event)}
                            >
                                <ExitToAppIcon />
                            </IconButton>
                        </Tooltip>

                        <Container style={{ flexDirection: 'column' }}>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    color: theme.palette.text.primary,
                                    fontWeight: 700,
                                }}
                            >
                                {event.lease_audit_schedule.name}
                            </span>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Event from schedule
                            </span>
                        </Container>
                    </Container>

                    <Container style={{ marginTop: theme.spacing(1) }}>
                        <Container style={{ flexDirection: 'column', flex: 1 }}>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Event Date
                            </span>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {new Date(
                                    event.event_date,
                                ).toLocaleDateString()}
                            </span>
                        </Container>

                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.primary,
                                fontWeight: 700,
                            }}
                        >
                            Status: {event.status}
                        </span>
                    </Container>
                </Container>
            )
        }

        if (schedule) {
            return (
                <Container
                    style={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        flexDirection: 'column',
                        padding: theme.spacing(1),
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                        }}
                    >
                        <Tooltip title="View Audits">
                            <IconButton onClick={() => onDetailClick(schedule)}>
                                <ExitToAppIcon />
                            </IconButton>
                        </Tooltip>

                        <Container style={{ flexDirection: 'column' }}>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    color: theme.palette.text.primary,
                                    fontWeight: 700,
                                }}
                            >
                                {schedule.name}
                            </span>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                {schedule.recurrence_pattern?.toLowerCase()}
                            </span>
                        </Container>
                    </Container>

                    <Container style={{ marginTop: theme.spacing(1) }}>
                        <Container style={{ flexDirection: 'column', flex: 1 }}>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                First Audit
                            </span>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {scheduleDate.toLocaleDateString()}
                            </span>
                        </Container>

                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.primary,
                                fontWeight: 700,
                            }}
                        >
                            {schedule.companies.length} companies
                        </span>
                    </Container>

                    <Container
                        style={{
                            flexDirection: 'column',
                            marginTop: theme.spacing(1),
                        }}
                    >
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {schedule.description}
                        </span>
                    </Container>
                </Container>
            )
        }

        if (leaseAudit) {
            return (
                <Container
                    style={{
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        flexDirection: 'column',
                        padding: theme.spacing(1),
                    }}
                >
                    <Container
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                        }}
                    >
                        <Tooltip title="View Audit Details">
                            <IconButton
                                onClick={() =>
                                    onLeaseAuditDetailClick(leaseAudit)
                                }
                            >
                                <ExitToAppIcon />
                            </IconButton>
                        </Tooltip>

                        <Container style={{ flexDirection: 'column' }}>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    color: theme.palette.text.primary,
                                    fontWeight: 700,
                                }}
                            >
                                {leaseAudit.name}
                            </span>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Stand alone audit
                            </span>
                        </Container>
                    </Container>

                    <Container style={{ marginTop: theme.spacing(1) }}>
                        <Container style={{ flexDirection: 'column', flex: 1 }}>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Created Date
                            </span>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    color: theme.palette.text.primary,
                                }}
                            >
                                {new Date(
                                    leaseAudit.created_date,
                                ).toLocaleDateString()}
                            </span>
                        </Container>
                    </Container>
                </Container>
            )
        }

        return null
    }

    // Render action buttons based on type
    const renderActions = () => {
        if (event) {
            const parentSchedule = event.lease_audit_schedule
            const isPaused = parentSchedule.status === 'PAUSED'
            const isPastDate = new Date() > new Date(event.event_date)

            return (
                <Container
                    style={{
                        padding: theme.spacing(1),
                        justifyContent: 'space-between',
                    }}
                >
                    {!isPastDate && (
                        <Tooltip title="Add Schedule">
                            <IconButton onClick={onScheduleClick}>
                                <AddIcon color="primary" />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip
                        title={isPaused ? 'Resume Schedule' : 'Pause Schedule'}
                    >
                        <IconButton
                            onClick={() =>
                                isPaused
                                    ? onResumeSchedule?.(parentSchedule)
                                    : onPauseSchedule?.(parentSchedule)
                            }
                        >
                            {isPaused ? (
                                <PlayArrowIcon color="primary" />
                            ) : (
                                <PauseIcon color="error" />
                            )}
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Schedule">
                        <IconButton
                            onClick={() => onDeleteSchedule?.(parentSchedule)}
                        >
                            <DeleteIcon color="error" />
                        </IconButton>
                    </Tooltip>
                </Container>
            )
        }

        // Check if the date is in the past for non-event cases
        const isPastDate = formState.date ? new Date() > formState.date : false

        if (schedule) {
            const isPaused = schedule.status === 'PAUSED'
            return (
                <Container
                    style={{
                        padding: theme.spacing(1),
                        justifyContent: 'space-between',
                    }}
                >
                    {!isPastDate && (
                        <Tooltip title="Add Schedule">
                            <IconButton onClick={onScheduleClick}>
                                <AddIcon color="primary" />
                            </IconButton>
                        </Tooltip>
                    )}

                    <Tooltip
                        title={isPaused ? 'Resume Schedule' : 'Pause Schedule'}
                    >
                        <IconButton
                            onClick={() =>
                                isPaused
                                    ? onResumeSchedule?.(schedule)
                                    : onPauseSchedule?.(schedule)
                            }
                        >
                            {isPaused ? (
                                <PlayArrowIcon color="primary" />
                            ) : (
                                <PauseIcon color="error" />
                            )}
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete Schedule">
                        <IconButton
                            onClick={() => onDeleteSchedule?.(schedule)}
                        >
                            <DeleteIcon color="error" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Edit Schedule">
                        <IconButton onClick={() => onEditClick(schedule)}>
                            <EditIcon color="secondary" />
                        </IconButton>
                    </Tooltip>
                </Container>
            )
        }

        if (leaseAudit) {
            return (
                <Container
                    style={{
                        padding: theme.spacing(1),
                        justifyContent: 'space-between',
                    }}
                >
                    <Tooltip title="Add Schedule">
                        <IconButton onClick={onScheduleClick}>
                            <AddIcon color="primary" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Edit Schedule">
                        <IconButton
                            onClick={() => {
                                if (schedule) {
                                    onEditClick(schedule)
                                }
                            }}
                        >
                            <EditIcon color="secondary" />
                        </IconButton>
                    </Tooltip>
                </Container>
            )
        }

        // Default actions for empty date click
        return (
            <Container
                style={{
                    padding: theme.spacing(1),
                    justifyContent: 'space-between',
                }}
            >
                {!isPastDate && (
                    <Tooltip title="Add Schedule">
                        <IconButton onClick={onScheduleClick}>
                            <AddIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                )}
            </Container>
        )
    }

    return (
        <Popover
            anchorReference="anchorPosition"
            anchorPosition={{
                top: formState.yPos,
                left: formState.xPos,
            }}
            open={formState.open}
            onClose={onClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
            <Container
                style={{
                    flexDirection: 'column',
                    width: 300,
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                <Container
                    style={{
                        alignItems: 'center',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(1),
                    }}
                >
                    <Typography
                        variant="body1"
                        style={{
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                        }}
                    >
                        {formState.date?.toLocaleDateString()}
                    </Typography>

                    <div style={{ flex: 1 }} />
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Container>

                {/* Content */}
                {renderContent()}

                {/* Actions */}
                {renderActions()}
            </Container>
        </Popover>
    )
}
